'use client'

import dynamic from 'next/dynamic'
import { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, Container, Fade, Portal, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material'

// Types
import type { SolutionsMegaMenuProps } from '@/types/components/organisms/mega-menu/solutions'

// Helpers
import { CLOSE_DELAY, backdropSx, dimSiblingsSx, triggerSx } from '@/helpers/mega-menu'

// Utils
import { localizeHref } from '@/utils/theme/nav-menu'

// Components
// The arrow is imported directly (not with dynamic) so the button never suspends and shifts the header
import ArrowDownIconAtom from '@/components/atoms/icon/two-tone/arrow-down'
const MegaMenuItemMolecule = dynamic(() => import('@/components/molecules/mega-menu/item'))
const MegaMenuMobileItemMolecule = dynamic(() => import('@/components/molecules/mega-menu/mobile-item'))
const MegaMenuPlatformsMolecule = dynamic(() => import('@/components/molecules/mega-menu/platforms'))

const SolutionsMegaMenuOrganism = (props: SolutionsMegaMenuProps) => {
	// Props
	const { lng, locale, top, variant } = props

	// Variables
	const solutions = locale.common.solutions
	const [open, setOpen] = useState(false)
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

	// Callbacks
	const show = useCallback(() => {
		if (closeTimer.current) clearTimeout(closeTimer.current)
		setOpen(true)
	}, [])

	const hide = useCallback(() => {
		if (closeTimer.current) clearTimeout(closeTimer.current)
		closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY)
	}, [])

	useEffect(() => {
		if (!open) return
		const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [open])

	useEffect(() => () => void (closeTimer.current && clearTimeout(closeTimer.current)), [])

	if (variant === 'mobile') {
		return (
			<Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&::before': { display: 'none' } }}>
				<AccordionSummary expandIcon={<ArrowDownIconAtom />} sx={{ px: 1, '& .MuiTypography-root': { fontSize: 16, fontWeight: 600 } }}>
					<Typography>{solutions.title}</Typography>
				</AccordionSummary>

				<AccordionDetails sx={{ px: 1 }}>
					{solutions.groups.map((group, groupIndex) => (
						<Box key={group.title} sx={{ mb: 2 }}>
							<Typography sx={{ fontSize: 13, fontWeight: 600, color: 'grey.600', mb: 0.5 }}>{group.title}</Typography>

							{group.items.map(item => (
								<MegaMenuMobileItemMolecule
									key={item.title}
									href={localizeHref(lng, item.href)}
									icon={item.icon}
									title={item.title}
									badge={'soon' in item && item.soon ? solutions.comingSoon : undefined}
								/>
							))}

							{groupIndex === 0 && <MegaMenuPlatformsMolecule label={solutions.availableOn} platforms={solutions.platforms} />}
						</Box>
					))}
				</AccordionDetails>
			</Accordion>
		)
	}

	return (
		<Box onMouseEnter={show} onMouseLeave={hide}>
			<Button
				aria-haspopup="true"
				aria-expanded={open}
				// Click only opens: hover has already opened the menu, so toggling here would close it again
				onClick={show}
				endIcon={<ArrowDownIconAtom sx={{ fontSize: '16px !important', transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none' }} />}
				sx={triggerSx(open)}
			>
				{solutions.title}
			</Button>

			{/* Anything below that suspends only holds back the panel, not the button or the neighboring links */}
			<Suspense fallback={null}>
				<Portal>
					{/* Blurred backdrop under the app bar */}
					<Fade in={open} timeout={200} unmountOnExit>
						<Box onClick={() => setOpen(false)} sx={backdropSx(top)} />
					</Fade>

					<Fade in={open} timeout={200} unmountOnExit>
						<Box onMouseEnter={show} onMouseLeave={hide} sx={{ position: 'fixed', left: 0, right: 0, top: `${top}px`, zIndex: t => t.zIndex.appBar - 1, pt: 1 }}>
							<Container maxWidth="lg">
								<Box
									role="menu"
									aria-label={solutions.title}
									sx={{
										display: 'grid',
										gridTemplateColumns: 'repeat(4, 1fr)',
										gap: 1,
										p: 2,
										bgcolor: 'common.white',
										borderRadius: 3,
										boxShadow: '0 24px 48px -12px rgba(23, 23, 23, 0.18)'
									}}
								>
									{solutions.groups.map((group, groupIndex) => (
										<Box key={group.title} sx={{ p: 2, ...dimSiblingsSx }}>
											<Typography sx={{ fontSize: 13, fontWeight: 600, color: 'grey.600', mb: 1.5 }}>{group.title}</Typography>

											{group.items.map(item => (
												<MegaMenuItemMolecule
													key={item.title}
													href={localizeHref(lng, item.href)}
													icon={item.icon}
													title={item.title}
													description={'description' in item ? item.description : undefined}
													badge={'soon' in item && item.soon ? solutions.comingSoon : undefined}
													onClick={() => setOpen(false)}
												/>
											))}

											{groupIndex === 0 && <MegaMenuPlatformsMolecule label={solutions.availableOn} platforms={solutions.platforms} />}
										</Box>
									))}
								</Box>
							</Container>
						</Box>
					</Fade>
				</Portal>
			</Suspense>
		</Box>
	)
}

export default SolutionsMegaMenuOrganism
